/* BgCalibrationPanel — Layer D admin dashboard.
 *
 * Lets an admin:
 *   1. See the international reference biomarkers (seed)
 *   2. Paste/upload a CSV of a BG cohort and see per-biomarker drift
 *   3. Review past uploads and drill into their report
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, RefreshCw, Trash2, Flag, CheckCircle2, AlertTriangle, HelpCircle } from "lucide-react";

const FLAG_STYLE = {
  aligned:                   { bg: "#DCFCE7", fg: "#065F46", icon: CheckCircle2, label: "съгласувано" },
  minor_drift:               { bg: "#FEF3C7", fg: "#92400E", icon: AlertTriangle, label: "леко отклонение" },
  recalibration_suggested:   { bg: "#FEE2E2", fg: "#991B1B", icon: Flag, label: "нужна калибрация" },
  insufficient_data:         { bg: "#F3F4F6", fg: "#6B7280", icon: HelpCircle, label: "n < 30" },
};

function FlagPill({ flag }) {
  const s = FLAG_STYLE[flag] || FLAG_STYLE.insufficient_data;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: s.bg, color: s.fg }} data-testid={`bg-flag-${flag}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function BiomarkerRow({ bm }) {
  const intl = bm.international_reference?.p50 ?? bm.international_reference?.p50_male ?? bm.international_reference?.p50_female ?? "—";
  return (
    <tr className="border-t text-xs" data-testid={`bg-row-${bm.key}`}>
      <td className="py-2 font-medium">{bm.label_bg} <span className="text-gray-400 text-[10px]">({bm.unit})</span></td>
      <td className="text-center text-gray-500">{intl}</td>
      <td className="text-center font-semibold" style={{ color: "#0EA5E9" }}>{bm.local?.p50 ?? "—"}</td>
      <td className="text-center text-gray-500">{bm.local?.n ?? 0}</td>
      <td className="text-center">{bm.delta_sd !== null && bm.delta_sd !== undefined ? bm.delta_sd.toFixed(2) : "—"}</td>
      <td className="text-center"><FlagPill flag={bm.flag} /></td>
      <td className="text-[10px] text-gray-400">
        {bm.suggested_cutoffs
          ? Object.entries(bm.suggested_cutoffs).slice(0, 2).map(([k, v]) => `${k}: ${typeof v === "number" ? v.toFixed(1) : JSON.stringify(v)}`).join(", ")
          : "—"}
      </td>
    </tr>
  );
}

export default function BgCalibrationPanel({ API, isDark }) {
  const [biomarkers, setBiomarkers] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [activeUploadId, setActiveUploadId] = useState(null);

  const token = useMemo(() => localStorage.getItem("token") || sessionStorage.getItem("nutramilo_access_token") || "", []);
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: seed }, { data: upl }] = await Promise.all([
        axios.get(`${API}/admin/bg-calibration/biomarkers`, { headers: auth }),
        axios.get(`${API}/admin/bg-calibration/uploads`, { headers: auth }),
      ]);
      setBiomarkers(seed.biomarkers || []);
      setUploads(upl.uploads || []);
      // Auto-load most recent upload into the grid
      if ((upl.uploads || []).length > 0 && !activeUploadId) {
        const latestId = upl.uploads[0].id;
        setActiveUploadId(latestId);
        const { data: r } = await axios.get(`${API}/admin/bg-calibration/report?upload_id=${latestId}`, { headers: auth });
        setReport(r.report);
      }
    } catch {
      toast.error("Грешка при зареждане на BG калибрация");
    } finally {
      setLoading(false);
    }
  }, [API, auth, activeUploadId]);

  useEffect(() => { load(); }, [load]);

  const selectUpload = async (id) => {
    setActiveUploadId(id);
    try {
      const { data } = await axios.get(`${API}/admin/bg-calibration/report?upload_id=${id}`, { headers: auth });
      setReport(data.report);
    } catch { toast.error("Грешка при зареждане на report"); }
  };

  const submit = async () => {
    if (!csvText.trim() || !label.trim()) {
      toast.error("CSV и label са задължителни");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/bg-calibration/upload`,
        { csv: csvText, label, notes: notes || null },
        { headers: auth });
      toast.success(`Качено — ${data.n_rows} реда`);
      setCsvText(""); setLabel(""); setNotes("");
      setReport(data.report);
      setActiveUploadId(data.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при upload");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Изтриване на upload?")) return;
    try {
      await axios.delete(`${API}/admin/bg-calibration/upload/${id}`, { headers: auth });
      toast.success("Изтрито");
      if (id === activeUploadId) { setReport(null); setActiveUploadId(null); }
      load();
    } catch { toast.error("Грешка"); }
  };

  const rows = report?.biomarkers || biomarkers.map(bm => ({ ...bm, local: { n: 0 }, flag: "insufficient_data" }));
  const summary = report?.summary;

  return (
    <div className="space-y-4" data-testid="bg-calibration-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">BG population calibration</h3>
          <p className="text-xs text-gray-500">Layer D · международни cutoffs vs български кохорт · per-biomarker drift</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="bg-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Опресни
        </Button>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-4 gap-2">
          {[
            { k: "aligned", label: "Съгласувани", color: "#10B981" },
            { k: "minor_drift", label: "Леки отклонения", color: "#F59E0B" },
            { k: "recalibration_suggested", label: "Нужна калибрация", color: "#EF4444" },
            { k: "insufficient_data", label: "n < 30", color: "#6B7280" },
          ].map(({ k, label, color }) => (
            <Card key={k} className={isDark ? "bg-gray-800 border-gray-700" : ""}>
              <CardContent className="p-3 text-center">
                <div className="text-[10px] text-gray-500">{label}</div>
                <div className="text-2xl font-bold" style={{ color }} data-testid={`bg-summary-${k}`}>{summary[k]}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Biomarker grid */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
        <CardContent className="p-4">
          <h4 className="font-semibold mb-3">Биомаркери — 15 общо</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left py-1">Биомаркер</th>
                  <th>Intl p50</th>
                  <th>BG p50</th>
                  <th>n</th>
                  <th>Δ SD</th>
                  <th>Статус</th>
                  <th className="text-left">Предложени локални cutoffs</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(bm => <BiomarkerRow key={bm.key} bm={bm} />)}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Upload form */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""} data-testid="bg-upload-form">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold flex items-center gap-2"><Upload className="w-4 h-4" /> Нов upload (CSV)</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-gray-500">Label</label>
              <Input value={label} onChange={e => setLabel(e.target.value)}
                placeholder="напр. BG-Sofia-Q1-2026" className="h-9 text-sm" data-testid="bg-form-label" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Бележки</label>
              <Input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="по избор" className="h-9 text-sm" data-testid="bg-form-notes" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-gray-500">CSV (header = biomarker keys; разделител , или ;)</label>
            <textarea value={csvText} onChange={e => setCsvText(e.target.value)}
              placeholder={"hba1c,ldl,bmi\n5.4,110,24.3\n6.0,125,26.1\n..."}
              rows={8}
              className="w-full text-xs border rounded p-2 font-mono" data-testid="bg-form-csv" />
            <p className="text-[10px] text-gray-400 mt-1">
              Валидни колони: {biomarkers.map(b => b.key).join(", ")}
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving || !csvText.trim() || !label.trim()}
              className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="bg-form-submit">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
              Upload & изчисли
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload history */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
        <CardContent className="p-4">
          <h4 className="font-semibold mb-3">История на uploads ({uploads.length})</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left py-1">Дата</th>
                  <th className="text-left">Label</th>
                  <th>n реда</th>
                  <th>Aligned</th>
                  <th>Drift</th>
                  <th>Recal.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {uploads.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-gray-400 py-6">Все още няма uploads</td></tr>
                )}
                {uploads.map(u => (
                  <tr key={u.id} className={`border-t cursor-pointer ${u.id === activeUploadId ? "bg-sky-50/50" : ""}`}
                    onClick={() => selectUpload(u.id)} data-testid={`bg-upload-${u.id}`}>
                    <td className="py-1.5 text-gray-500">{new Date(u.created_at).toLocaleDateString("bg-BG")}</td>
                    <td className="font-medium">{u.label}</td>
                    <td className="text-center">{u.n_rows}</td>
                    <td className="text-center text-green-600">{u.summary?.aligned ?? 0}</td>
                    <td className="text-center text-amber-600">{u.summary?.minor_drift ?? 0}</td>
                    <td className="text-center text-red-600">{u.summary?.recalibration_suggested ?? 0}</td>
                    <td className="text-right">
                      <button onClick={(e) => { e.stopPropagation(); del(u.id); }}
                        className="text-red-500 hover:text-red-700" data-testid={`bg-del-${u.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
